/**
 * Duplicate Actions - Snippet duplication with UI integration
 *
 * This action handles snippet duplication by creating a new snippet with the same
 * content (name, body, description from the duplicate modal) via the create API.
 * - Creates the new snippet via createSnippet
 * - Shows success/error flags (duplicate-specific wording)
 * - Adds the new snippet to the editor's quick insert menu
 * - Refreshes the snippets list
 */
import type { StoreActionApi } from 'react-sweet-state';

import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { duplicateSnippetModalMessages } from '../../modals/DuplicateSnippetModal.tsx';
import type { Snippet } from '../../snippetTypes.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';

import {
	saveSnippetWithUI,
	type SnippetSaveOptions,
	type SnippetSaveMessages,
} from './actionsHelper.ts';

export type DuplicateSnippetOptions = SnippetSaveOptions;

const duplicateSaveMessages: SnippetSaveMessages = {
	successFlagTitle: duplicateSnippetModalMessages.duplicateSuccessFlagTitle,
	errorFlagTitle: duplicateSnippetModalMessages.duplicateErrorFlagTitle,
	errorFlagDescription: duplicateSnippetModalMessages.duplicateErrorFlagDescription,
};

/**
 * Actions for duplicating snippets with UI integration.
 */
export const duplicateActions = {
	/**
	 * Duplicates a snippet and refreshes the snippets list.
	 *
	 * After successful duplication:
	 * - Clears the service cache and fetches the updated list
	 * - Shows a success flag (if options provided)
	 * - Adds the new snippet to the quick insert menu
	 *
	 * On error:
	 * - Reports to monitoring
	 * - Shows an error flag (if options provided)
	 * - Returns null (does not throw when options provided)
	 *
	 * @param name - The name for the duplicated snippet
	 * @param body - The ADF content body
	 * @param description - Optional description text
	 * @param options - UI options including flags, intl, and editor API ref
	 * @returns Promise resolving to the duplicated snippet, or null on failure
	 */
	duplicateSnippet:
		(name: string, body: ADFEntity, description?: string, options?: DuplicateSnippetOptions) =>
		async (storeApi: StoreActionApi<SnippetsState>): Promise<Snippet | null> =>
			saveSnippetWithUI(name, body, description, options, duplicateSaveMessages, storeApi),
};
